/**
 * Phone numbers everywhere in the app read XXX-XXX-XXXX (Jim 2026-09-22,
 * JIM-0922-03: "All phone numbers within the entire app should be formatted
 * XXX-XXX-XXXX"). The API stores them that way too; these helpers cover
 * what is shown and what is typed.
 */

const MAX_DIGITS = 10;

/** The ten US digits in a phone string, dropping a leading country 1. */
export function phoneDigits(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

/**
 * Display form: XXX-XXX-XXXX for a ten-digit number. Anything else (an
 * extension, a foreign number, a partial) comes back as typed so nothing is
 * silently lost.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const d = phoneDigits(phone);
  if (d.length === MAX_DIGITS) {
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return phone;
}

/**
 * As-you-type form for an input: keeps only digits (at most ten) and drops
 * the dashes in as they are earned, so the field reads 512, 512-987,
 * 512-987-9020 while typing and pasting "(512) 987-9020" lands as
 * 512-987-9020. More than ten digits is left alone (extensions, +1 …).
 */
export function formatPhoneInput(raw: string): string {
  const all = raw.replace(/\D/g, '');
  const d = all.length === 11 && all.startsWith('1') ? all.slice(1) : all;
  if (d.length > MAX_DIGITS) return raw;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}
